import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';

import axios from 'axios';
import { useAccount } from 'wagmi';

import { useGlobalContext } from '../../AppContext';
import {
  add_favorite_token,
  add_favorite_whale,
  remove_favorite_token,
  remove_favorite_whale,
} from '../../utils/apiOperations';
import {
  calculateColumnWidth,
  notify,
  parseAmount,
  parseMoney,
  parsePercentage,
} from '../../utils/utils';
import { AddReminderToken } from '../../components/AddReminderToken';
import { BigMenu } from '../../components/BigMenu';

import './BuyAndSell.scss';

export const BuyAndSell = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);
  const { config, isMobile, favorites, whales, setFavorites, setWhales } = useGlobalContext();
  const { address: account } = useAccount();
  const [showTable, setShowTable] = useState<JSX.Element>(<></>);
  const [tableData, setTableData] = useState<TableDataProps[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [rawData, setRawData] = useState<any>(false);

  const [reminderCoingeckoId, setReminderCoingeckoId] = useState<string>();
  const [showAddNewReminder, setShowAddNewReminder] = useState<boolean>(false);

  useEffect(() => {
    if (config && config.top10) {
      setRawData(config.top10);
    } else {
      renderShowTable({});
    }
  }, [config]);

  const renderShowTable = (data: any): void => {
    setShowTable(
      <DataTable
        noDataComponent={
          <div className="data-table-no-record">There are no records to display.</div>
        }
        pagination
        columns={columns}
        data={data}
      />,
    );
  };

  const onClose = (): void => {
    setShowAddNewReminder(false);
  };

  const columns = [
    {
      name: 'Name',
      selector: (row: TableDataProps) => row.token,
      sortable: true,
      cell: (row: TableDataProps) => (
        <CustomNetwork name={row.token} logo={row.tokenLogo} ads={false} data={row} />
      ),
      width: calculateColumnWidth('buyAndSell', 'token'),
    },

    /*
    {
      name: 'Category',
      selector: (row: TableDataProps) => row.category,
      sortable: true,
      width: '140px',
    },
    */
    {
      name: 'Price',
      selector: (row: TableDataProps) => row.priceRaw,
      sortable: true,
      width: calculateColumnWidth('buyAndSell', 'price'),
      cell: (row: TableDataProps) => row.price,
    },
    {
      name: (
        <div className="pnl-head">
          <img src="/icons/graph.svg" className="head-image" /> 24H
        </div>
      ),
      selector: (row: TableDataProps) => row.pnl24,
      width: calculateColumnWidth('buyAndSell', '24h'),
      cell: (row: TableDataProps) => (
        <div
          className={`table-pnl-24 ${
            parseFloat(row.pnl24percentage) > 0
              ? 'green'
              : parseFloat(row.pnl24percentage) == 0
              ? ''
              : 'red'
          }`}
        >
          <span>{row.pnl24amount}</span>
          <strong>{row.pnl24percentage}</strong>
        </div>
      ),
    },
    {
      name: <img src="/icons/diamond.svg" className="head-image" />,
      selector: (row: TableDataProps) => row.holders,
      width: calculateColumnWidth('buyAndSell', 'holders'),
    },
    {
      name: 'Marketcap',
      selector: (row: TableDataProps) => row.marketcapRaw,
      sortable: true,
      width: calculateColumnWidth('buyAndSell', 'marketcap'),
      cell: (row: TableDataProps) => row.marketcap,
    },
    {
      name: '+',
      selector: (row: TableDataProps) => row.button,
      width: calculateColumnWidth('buyAndSell', 'action'),
      cell: (row: TableDataProps) => {
        return (
          <div className="table-actions">
            <img
              onClick={() => {
                if (!account) {
                  notify('You need to connect your wallet to use this feature.');
                } else {
                  if (favorites.tokens[row.coingeckoid]) {
                    remove_favorite_token(favorites.tokens[row.coingeckoid], setFavorites);
                  } else {
                    add_favorite_token(row.coingeckoid, setFavorites);
                  }
                }
              }}
              style={{ opacity: account ? 1 : 0.6 }}
              className={`${favorites.tokens && favorites.tokens[row.coingeckoid] ? 'active' : ''}`}
              src="/icons/star2.svg"
              alt=""
            />
            <img
              onClick={() => {
                if (!account) {
                  notify('You need to connect your wallet to use this feature.');
                } else {
                  if (whales[row.coingeckoid]) {
                    remove_favorite_whale(whales[row.coingeckoid], setWhales);
                  } else {
                    add_favorite_whale(row.coingeckoid, setWhales);
                  }
                }
              }}
              style={{ opacity: account ? 1 : 0.6 }}
              className={`${whales && whales[row.coingeckoid] ? 'active' : ''}`}
              src="/icons/whale-2.svg"
              alt=""
            />
            <img
              onClick={() => {
                if (!account) {
                  notify('You need to connect your wallet to use this feature.');
                } else {
                  setReminderCoingeckoId(row.coingeckoid);
                  setShowAddNewReminder(!showAddNewReminder);
                }
              }}
              style={{ opacity: account ? 1 : 0.6 }}
              src="/icons/reminder.svg"
              alt=""
            />
          </div>
        );
      },
    },
  ];

  interface TableDataProps {
    coingeckoid: string;
    token: string;
    symbol: string;
    tokenLogo: string;
    address: string;
    pnl24: string;
    pnl24amount: string;
    pnl24percentage: string;
    ratio: string;
    ratioRaw: number;
    color: string;
    holders: 0;
    high24h: string;
    low24h: string;
    price: string;
    priceRaw: number;
    ath: string;
    atl: string;
    marketcap: string;
    marketcapRaw: number;
    category: string;
    wallet?: string;
    button: boolean;
    uniqueMarket: any;
  }

  interface CustomNetworkProps {
    data: any;
    name: string;
    logo: string;
    ads?: boolean;
  }

  const CustomNetwork = (props: CustomNetworkProps) => {
    return (
      <>
        <div className="icon-container">
          <img className="tokenLogo" src={props.logo} />
        </div>
        {props.name}
        {props.data && props.data.uniqueMarket && (
          <div className="cex-details">
            {Object.keys(props.data.uniqueMarket)
              .slice(0, 4)
              .map((key: any, index: number) => {
                return (
                  <Link
                    key={index}
                    className="cex-link"
                    target="_blank"
                    to={{ pathname: props.data.uniqueMarket[key] }}
                  >
                    {key}
                  </Link>
                );
              })}
          </div>
        )}
        {props.ads && (
          <div
            className="adsBuyTableLink"
            onClick={(e: any) => e.target.classList.toggle('active')}
          >
            Buy
            <div className="details">
              <div
                className="close"
                onClick={(e: any) => e.target.parentNode.parentNode.classList.toggle('active')}
              >
                &times;
              </div>
              <div className="sponsored">
                <div className="sponsored-warning">
                  <img src="/images/icon-dark.svg" />
                  Sponsored
                </div>
                <div className="binance">
                  <div className="header-part">
                    <img src="/sponsored/binance/binance.png" /> Binance
                  </div>
                  <div className="description-part">
                    Buy Crypto with Fees as Low as 0%
                    <Link target="_blank" to={{ pathname: 'https://www.binance.com' }}>
                      Start Trading
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    renderShowTable(tableData);
    if (isMobile && document.getElementsByClassName('rdt_Table')[0]) {
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 50,
          behavior: 'smooth',
        });
      }, 200);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 60,
          behavior: 'smooth',
        });
      }, 210);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 70,
          behavior: 'smooth',
        });
      }, 220);

      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 0,
          behavior: 'smooth',
        });
      }, 800);
    }
  }, [tableData]);

  useEffect(() => {
    async function fetchData(): Promise<void> {
      try {
        const response = await axios({
          url: `${process.env.REACT_APP_API}/token-search.php`,
          params: {
            term: searchTerm,
          },
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
        });
        if (response && response.data && response.data.data && response.data.data.datas)
          setRawData(response.data.data.datas);
        else setRawData([]);
      } catch (error) {
        console.error(error);
      }
    }

    const timer = setTimeout(() => {
      if (searchTerm) fetchData();
      else if (config && config.top10) {
        setRawData(config.top10);
      } else {
        setRawData([]);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const tmpData: TableDataProps[] = new Array();
    Object.values(rawData).forEach((item: any) => {
      if (item.coingeckoid)
        tmpData.push({
          coingeckoid: item.coingeckoid,
          token: item.name,
          symbol: item.symbol,
          category: item.category,
          address: item.address,
          tokenLogo: item.logo,
          ratio: parsePercentage(item.ratio),
          ratioRaw: item.ratio,
          holders: item.holders,
          pnl24: parseMoney(item.price),
          pnl24amount: parseMoney(item.pnl24hamount),
          pnl24percentage: parsePercentage(item.pnl24hpercentage),
          high24h: item.high24h,
          low24h: item.low24h,
          price: parseMoney(item.price),
          priceRaw: parseFloat(item.price),
          ath: item.ath,
          atl: item.atl,
          marketcap: parseAmount(item.marketcap),
          marketcapRaw: parseFloat(item.marketcap),
          button: true,
          uniqueMarket: item.uniqueMarket,
          color:
            item.graphLastDirection === 'up'
              ? '90ff00'
              : item.graphLastDirection === 'down'
              ? 'ff0000'
              : 'cccccc',
        });
    });
    setTableData(tmpData);
  }, [rawData, favorites, whales]);

  const handleOnChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="buy-and-sell">
      <BigMenu horizontal={true} />
      <div className="table-container mt-5">
        <div className="search-input">
          <input
            type="text"
            placeholder="Start typing a token name or symbol to search..."
            value={searchTerm}
            onChange={handleOnChange}
          />
        </div>
        <div className={`custom-table-${theme} padding-left`}>
          <div className="table-border-top mt-2" />
          {showTable}
          <div className="table-border-bottom" />
        </div>
      </div>
      {showAddNewReminder && reminderCoingeckoId && (
        <AddReminderToken coingeckoid={reminderCoingeckoId} onClose={onClose} />
      )}
    </div>
  );
};
